/* eslint-disable no-shadow */
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMatch } from 'react-router';
import Area from '@rio-cloud/rio-uikit/Area';
import ChartTooltip from '@rio-cloud/rio-uikit/ChartTooltip';
import ComposedChart from '@rio-cloud/rio-uikit/ComposedChart';
import Legend from '@rio-cloud/rio-uikit/Legend';
import Line from '@rio-cloud/rio-uikit/Line';
import ReferenceLine from '@rio-cloud/rio-uikit/ReferenceLine';
import useOnScreen from '@rio-cloud/rio-uikit/useOnScreen';
import moment from 'moment';
import { Symbols } from 'recharts';

import { isWorkshopParentFrontend } from '@/config';
import { isFeatureEnabled } from '@/configuration/featureToggle/featureToggleSlice';
import { AnalyticsUtils } from '@/configuration/setup/googleAnalytics';
import { useAppSelector } from '@/configuration/setup/hooks';
import { useFetchHistoricalBatteryHealthQuery } from '@/features/battery/api/batterySlice';
import PackHistoryButton from '@/features/battery/workshop/PackHistoryButton';
import ConnectionErrorState from '@/features/batteryHistoryGraph/errorViews/ConnectionErrorState';
import HistoryNotAvailableErrorState from '@/features/batteryHistoryGraph/errorViews/HistoryNotAvailableErrorState';
import MileageBelow20KKmErrorState from '@/features/batteryHistoryGraph/errorViews/MileageBelow20KKmErrorState';
import { LoadingScreen } from '@/features/batteryOverview/utils/BatteryDataUtils';
import { BatteryHistoryGraphTooltip } from './BatteryHistoryGraphTooltip';

export const THRESHOLD_WARNING = 60;
export const THRESHOLD_DANGER = 55;

const renderCfeHeader = () => (
    <>
        <div className={'margin-bottom-25'}>
            <span
                className="text-color-black
                                   border border-color-black border-bottom-only border-width-3
                                   padding-x-25 padding-y-10
                                   text-size-14"
            >
                <FormattedMessage id="intl-msg:batteryService.batteryAgingHistory.title" />
            </span>
        </div>
        <div className={'margin-bottom-15'}>
            <span>
                <FormattedMessage id="intl-msg:batteryService.batteryAgingHistory.subTitle.extended" />
            </span>
        </div>
    </>
);

const renderWfeHeader = () => (
    <>
        <h4 className="margin-top-15">
            <FormattedMessage id="intl-msg:batteryService.batteryAgingHistory.title" />
        </h4>
        <span className="margin-bottom-15">
            <FormattedMessage id="intl-msg:batteryService.batteryAgingHistory.subTitle" />
        </span>
    </>
);

const renderLegendContent = (isWorkshopParentFE: boolean) => {
    const iconColor = isWorkshopParentFE ? '#333' : '#0057ff';
    return (
        <div className={'display-flex gap-15'}>
            <div className={'display-flex align-items-center'}>
                <svg width="18" height="18" className={'overflow-visible'}>
                    <Symbols
                        cx={9}
                        cy={9}
                        size={128}
                        sizeType="area"
                        type="square"
                        fill={iconColor}
                        stroke={iconColor}
                    />
                </svg>
                <span className={'text-color-black'}>
                    <FormattedMessage
                        id={`intl-msg:batteryService.batteryAgingHistory.legend.stateOfHealthPercentage`}
                    />
                </span>
            </div>
            <div className={'display-flex align-items-center'}>
                <svg width="18" height="10" className={'margin-right-4'}>
                    <line
                        x1="0"
                        y1="5"
                        x2="24"
                        y2="5"
                        stroke={iconColor}
                        strokeWidth="2"
                        strokeDasharray="0.5 5"
                        strokeDashoffset="2"
                        strokeLinecap="round"
                    />
                </svg>
                <span className={'text-color-black'}>
                    <FormattedMessage id={`intl-msg:batteryService.batteryAgingHistory.legend.missingData`} />
                </span>
            </div>
        </div>
    );
};

const elementFullyVisibleOnScreen = 0.99;

const dateOnXAxis = (timestamp: string) => moment(timestamp).format('D MMM');

const BatteryHistoryGraph = () => {
    const isWorkshopParentFE = isWorkshopParentFrontend();

    const elementRef = useRef<HTMLDivElement>(null);
    const isVisible = useOnScreen(elementRef, { threshold: elementFullyVisibleOnScreen });

    useEffect(() => {
        if (isVisible && isWorkshopParentFE) {
            AnalyticsUtils.gaPush({
                event: 'watch_battery_chart',
                parameters: {
                    element_name: 'BatteryHistoryGraph',
                    component_name: 'LineChart',
                    flow_name: 'view_workshop_battery_health_data',
                    trigger: 'visibility',
                },
                userProps: {},
            });
        }
    }, [isVisible]);

    const urlPath = isWorkshopParentFE ? '/:assetId' : '/dialog/:assetId';
    const assetId = useMatch(urlPath)?.params.assetId;
    const isPackHistoryEnabled = useAppSelector(isFeatureEnabled('enable_battery_pack_history'));

    const renderHeader = () => (
        <>
            {isWorkshopParentFE && renderWfeHeader()}
            {!isWorkshopParentFE && renderCfeHeader()}
        </>
    );

    const {
        isLoading,
        isError,
        data: historicalBatteryHealth,
        refetch,
    } = useFetchHistoricalBatteryHealthQuery(assetId!);

    if (isLoading) {
        return <LoadingScreen />;
    }

    if (isError) {
        return (
            <>
                {renderHeader()}
                <ConnectionErrorState reloadTriggerFunction={refetch} />;
            </>
        );
    }

    if (historicalBatteryHealth?.historical_data_status === 'INSUFFICIENT_DATA') {
        return (
            <>
                {renderHeader()}
                <MileageBelow20KKmErrorState />
            </>
        );
    } else if (historicalBatteryHealth?.historical_data_status === 'NO_DATA') {
        return (
            <>
                {renderHeader()}
                <HistoryNotAvailableErrorState />
            </>
        );
    }
    return (
        <>
            {renderHeader()}
            {historicalBatteryHealth?.battery_health_records && (
                <div ref={elementRef} className="height-400">
                    <ComposedChart
                        tooltip={
                            <ChartTooltip
                                content={
                                    <BatteryHistoryGraphTooltip batteryType={historicalBatteryHealth.battery_type} />
                                }
                            />
                        }
                        legend={
                            <Legend
                                layout="horizontal"
                                verticalAlign="bottom"
                                align="left"
                                height={30}
                                wrapperStyle={{
                                    marginLeft: 60,
                                }}
                                content={() => renderLegendContent(isWorkshopParentFE)}
                            />
                        }
                        margin={{ left: -20, top: 5, right: 5, bottom: 0 }}
                        data={historicalBatteryHealth.battery_health_records}
                        dataKey={(entry: { timestamp: string }) => entry.timestamp}
                        showYAxis
                        showGrid
                        dataUnit="%"
                        lines={[
                            <Line
                                key="dashLine"
                                dataKey="state_of_health_percentage"
                                dot={false}
                                activeDot={{ r: 5 }}
                                strokeWidth={3}
                                strokeColor={isWorkshopParentFE ? 'gray-darkest' : 'brand-primary'}
                                strokeDasharray="0.5 10"
                                strokeLinecap="round"
                                type="linear"
                                connectNulls
                            />,
                            <Line
                                key="solidLine"
                                dataKey="state_of_health_percentage"
                                dot={false}
                                activeDot={{ r: 5 }}
                                strokeWidth={3}
                                strokeColor={isWorkshopParentFE ? 'gray-darkest' : 'brand-primary'}
                                type="linear"
                                strokeLinecap="square"
                            />,
                        ]}
                        areas={[
                            <Area
                                key="thresholdWarningArea"
                                stroke="none"
                                fill="brand-warning-decent"
                                fillOpacity={1}
                                dataKey="threshold_warning"
                                activeDot={false}
                                isAnimationActive={false}
                            />,
                            <Area
                                key="thresholdDangerArea"
                                stroke="none"
                                fill="brand-danger-decent"
                                fillOpacity={1}
                                dataKey="threshold_danger"
                                activeDot={false}
                                isAnimationActive={false}
                            />,
                        ]}
                        referenceLines={[
                            <ReferenceLine
                                key="warningReferenceLine"
                                y={THRESHOLD_WARNING}
                                label={{
                                    value: `${THRESHOLD_WARNING}%`,
                                    position: 'insideBottomRight',
                                    fill: '#f88e3c',
                                }}
                                stroke="brand-warning"
                                position="end"
                                strokeWidth={1}
                            />,
                            <ReferenceLine
                                key="dangerReferenceLine"
                                y={THRESHOLD_DANGER}
                                label={{
                                    value: `${THRESHOLD_DANGER}%`,
                                    position: 'insideBottomRight',
                                    fill: '#e22936',
                                }}
                                stroke="brand-danger"
                                strokeWidth={1}
                            />,
                        ]}
                        xAxisOptions={{ interval: 'preserveStartEnd', tickFormatter: dateOnXAxis }}
                        yAxisOptions={{ domain: [0, 100], allowDecimals: true, tickCount: 10 }}
                    />
                </div>
            )}
            {isPackHistoryEnabled && assetId && historicalBatteryHealth?.historical_data_status === 'OK' && (
                <PackHistoryButton assetId={assetId} />
            )}
        </>
    );
};

export default BatteryHistoryGraph;
